import { html, View } from 'rune-ts';
import {
  CanvasScaleOptions,
  CvImage,
  FlexiMakerData,
  FlexiMakerParams,
  MarkerPositionData,
  ProductColorFromMaker,
  Size,
  SvgProductMaterials,
} from './flexmaker.types';
import {
  FlexiMakerDomUtils,
  FlexiMakerUtils,
  getStrokeStyle,
} from '@modules/NewMaker/FlexiMaker/F/Function/flexmaker.helper.ts';
import { CONFIG } from '@modules/NewMaker/FlexiMaker/F/Function/uploadType';
import { type PrintablesResult, Stickerizer } from '@modules/Vector/F/Function/stickerizer/stickerizer.ts';
import { evaluateStickerSizeWithGangType } from '@modules/Df/TechPack/S/Function/jogakSticker.ganging.helper.ts';
import {
  INITIAL_SIZE_RANGE,
  JOGAK_STIKER_PRODUCT,
  MAX_DIMENSION,
  MIN_DIMENSION,
  PLUSH_PRODUCT,
  TTIBU_PRODUCT,
} from '@modules/NewMaker/FlexiMaker/S/Constant/fleximaker';

import {
  ApiAbortError,
  ApiNetworkError,
  ApiRequestError,
  ApiTimeoutError,
} from '@modules/Vector/F/Function/stickerizer/stickerizer.error.ts';
import { SizeRange } from '@modules/NewMaker/UI/sizeGauge/CalcualteSize.ts';
import { pickProfile } from '@modules/NewMaker/FlexiMaker/F/Function/fleximaker.profile.ts';
import { FlexiBaseProductId } from '@modules/Vector/S/Constant/stickerizer.constant.ts';

/**
 * FlexiMaker - 조각스티커 에디터
 */

export const STROKE_STYLE = {
  strokeColor: '#000',
  strokeWidth: 3,
  strokeDashArray: [12, 14] as [number, number],
};

export class FlexiMaker extends View<FlexiMakerData> {
  // eslint-disable-next-line no-use-before-define
  private static _instance: FlexiMaker | null = null;

  private _thumbnail_svg: string | null = null;
  private _svg_product_materials: SvgProductMaterials = {};
  private _stickerizer: Stickerizer | null = null;
  private _printable: PrintablesResult | null = null;
  private _size_range: SizeRange | undefined = undefined;
  private _input_size: Size | undefined = undefined;
  private _slider_value: number | undefined = undefined;
  private _is_background_fixed: boolean;
  private _use_stroke_dash: boolean;

  private _bp_id: FlexiBaseProductId | undefined = undefined;

  private constructor(data: FlexiMakerParams) {
    super({
      cv_image: null,
      ...data,
    });

    this._bp_id = data?.bp_id;
    this._size_range = INITIAL_SIZE_RANGE;
    this._stickerizer = data.stickerizer ?? null;
    const profile = pickProfile({ bpId: this._bp_id });
    this._is_background_fixed = profile.is_background_fixed;
    this._use_stroke_dash = G.collabo_type === '' && [JOGAK_STIKER_PRODUCT.ID].includes(this._bp_id!);
  }

  public static getInstance(data = {}): FlexiMaker {
    if (!FlexiMaker._instance) {
      FlexiMaker._instance = new FlexiMaker(data);
    }
    return FlexiMaker._instance;
  }

  public static resetInstance() {
    FlexiMaker._instance = null;
  }

  get profile() {
    return pickProfile({ bpId: this.bpId });
  }

  get profileCustomOption() {
    return this.profile.customOption?.();
  }

  get isPlush() {
    return this.bpId === PLUSH_PRODUCT.ID;
  }

  get isTtibu() {
    return this.bpId === TTIBU_PRODUCT.ID;
  }

  get bpId() {
    return this._bp_id;
  }

  get svgProductMaterial() {
    return this._svg_product_materials;
  }

  get cvImage() {
    return this.data.cv_image?._data ?? null;
  }

  get imageUrl() {
    if (!this.data.cv_image) return null;
    return this.cvImage?.image_url;
  }

  get imageSize() {
    const initial_size = this.profile.initial_size || { width: MAX_DIMENSION, height: MIN_DIMENSION };
    return this.cvImage?.flexi_size ?? { ...initial_size, is_default: true }; // 초기 sizeGuage를 띄우기 위한 기본 값
  }

  get sizeStr() {
    return `${Math.round(this.imageSize.width)} x ${Math.round(this.imageSize.height)} mm`;
  }

  get inputSize() {
    return this.imageSize?.input_size;
  }

  get offsetDistance() {
    return this.imageSize?.offset || 2;
  }

  get materialSource() {
    return this.imageSize?.meterial;
  }

  get hasImage() {
    return !!this.data.cv_image;
  }

  get sizeRange() {
    return this._size_range;
  }

  get stickerizer() {
    return this._stickerizer;
  }

  get sliderValue() {
    return this.imageSize?.slider_value ?? this._slider_value ?? 0.5;
  }

  override template() {
    const wrapper = document.querySelector('.canvas_zoom_wrapper') as HTMLElement;
    if (!wrapper) return html``;
    const {
      guide_image_url,
      previewImage = {
        imageUrl: CONFIG.IMAGE_SRC,
        alt: CONFIG.IMAGE_ALT,
      },
    } = this.profile;

    return html`
      <div class="flexi-maker">
        ${this.hasImage
          ? html` <div class="flexi-maker-board" data-is_background_fixed="${this._is_background_fixed}">
              ${html.preventEscape(this._thumbnail_svg || '')}
              ${guide_image_url
                ? html`
                    <img class="flexi-maker-board__guide-line" src="${guide_image_url}" alt="가이드 이미지" />
                  `
                : ``}
            </div>`
          : html`
              <div class="flexi-maker-preview ${this.profile.name === 'plush' ? 'is_plush' : ''}">
                <div class="flexi-maker-preview__text">${html.preventEscape(TT('flexi_maker::desc'))}</div>
                <div class="flexi-maker-preview__image">
                  <img src="${previewImage.imageUrl}" alt="${previewImage.alt}" />
                </div>
                <div class="flexi-maker-preview__upload-icon"></div>
              </div>
            `}
      </div>
    `;
  }

  updateData(new_data: Partial<FlexiMakerData>, stickerizer: Stickerizer | null) {
    Object.assign(this.data, new_data);
    this._stickerizer = stickerizer;
    this._size_range = this._getSizeRange();
  }

  isCahcedSize(slider_value: number) {
    if (!this.stickerizer || !this._validateStickerizer(this.stickerizer)) {
      console.error('stickerizer 없음 ');
      return false;
    }
    if (slider_value === undefined || slider_value === null) return false;
    const size = this.getSizeFromSliderValue(slider_value);
    if (!size) return false;
    return this.stickerizer.isProcessResultCached(size, this.offsetDistance);
  }

  getSizeFromSliderValue(slider_value: number) {
    if (!this.stickerizer || !this._validateStickerizer(this.stickerizer)) {
      console.error('stickerizer 없음 ');
      return;
    }
    const { getSizeAtNormalizedScale } = this.stickerizer.getMinMaxScale(this.offsetDistance);
    return getSizeAtNormalizedScale(slider_value);
  }

  async setImageUrl(
    cv_image: CvImage,
    options: { createInitialSize: boolean; stickerizer: Stickerizer; force_process: boolean },
  ) {
    const prev_cv_image = this.data.cv_image;
    const prev_stickerizer = this.stickerizer;
    const prev_sizeRange = this.sizeRange;

    try {
      const { createInitialSize, stickerizer: preload_stickerizer, force_process = true } = options;
      this.data.cv_image = cv_image;

      const stickerizer = preload_stickerizer || (await this._getStickerizer());

      if (!stickerizer || !this._validateStickerizer(stickerizer)) {
        console.error('stickerizer 없음 ');
        return;
      }

      const trimmed_img = stickerizer?.preprocessedInfo?.trimmed_image;
      if (!trimmed_img) {
        console.error('trimmed image 없음 ');
        return;
      }

      this._stickerizer = stickerizer;
      this._size_range = this._getSizeRange();
      const initial_size = this.profile.initial_size?.scale || 0.5;
      const slider_value = createInitialSize ? initial_size : this.sliderValue;

      this._slider_value = slider_value;

      const current_size = this.getSizeFromSliderValue(slider_value);

      if (!current_size) {
        console.error('current_size 없음 ');
        return;
      }
      FlexiMakerUtils.setCvImageSize(cv_image, {
        width: current_size.width,
        height: current_size.height,
        slider_value,
        ...(this.profile?.calOffset ? { offset: this.profile.calOffset(slider_value) } : {}),
      });

      let real_size;
      if (!force_process && !createInitialSize) {
        real_size = this.imageSize;
      } else {
        real_size = await this.processStickerizer(current_size);
      }

      this.redraw();

      return real_size;
    } catch (error) {
      this.data.cv_image = prev_cv_image;
      this._stickerizer = prev_stickerizer;
      this._size_range = prev_sizeRange;

      throw error;
    } finally {
      FlexiMakerDomUtils.switchUploadIcon(this.hasImage);
    }
  }

  async processStickerizer(current_size: Size | undefined, slider_value?: number) {
    const prev_input_size = this._input_size;
    const prev_thumbnail_svg = this._thumbnail_svg;
    const prev_flexi_size = this.data.cv_image?._data?.flexi_size
      ? { ...this.data.cv_image._data.flexi_size }
      : undefined;

    const rollback = () => {
      this._input_size = prev_input_size;
      this._thumbnail_svg = prev_thumbnail_svg;
      if (prev_flexi_size && this.data.cv_image?._data) {
        this.data.cv_image._data.flexi_size = prev_flexi_size;
      }
    };

    try {
      if (!this.stickerizer || !current_size) {
        console.error('Stickerizer 없음.');
        rollback();
        return;
      }

      const actual_slider_value = slider_value ?? this.sliderValue;
      this._input_size = current_size;
      const {
        dimension: {
          mm: { width, height },
        },
        thumbnail_svg,
      } = await this.stickerizer.process({
        targetSizeMm: current_size,
        strokeStyle: STROKE_STYLE,
        materialSource: this?.materialSource,
        ...this.profile.stickerizerOptions?.(),
        offsetDistanceMm: this.offsetDistance,
      });

      if (!this.bpId) throw new Error('bpId 없음');

      const { rotate, ganging_type } = evaluateStickerSizeWithGangType({ width, height }, this.bpId);
      const size_info = {
        width,
        height,
        rotate,
        ganging_type,
        slider_value: actual_slider_value,
        input_size: this._input_size,
        ...(this.profile?.calOffset ? { offset: this.profile.calOffset(actual_slider_value) } : {}),
      };

      this._thumbnail_svg = thumbnail_svg;

      const hasPathWithD = /<path\b[^>]*\bd\s*=\s*["'][^"']+["']/i.test(this._thumbnail_svg);

      if (!hasPathWithD) {
        throw new Error('Invalid SVG: path without d');
      }

      FlexiMakerUtils.setCvImageSize(this.data.cv_image, size_info);

      return size_info;
    } catch (error) {
      // 에러 발생 시 이전 상태로 복원
      rollback();

      if (error instanceof ApiAbortError) {
        console.warn('API 요청이 중단됨', error);
        return null;
      }

      if (error instanceof ApiNetworkError || error instanceof ApiTimeoutError) {
        console.warn('네트워크 오류가 발생했습니다. 인터넷 연결을 확인해주세요.');
        return null;
      }

      if (error instanceof ApiRequestError) {
        throw error;
      }
      throw error;
    }
  }

  setSizeGuide() {
    const size_guide_el = FlexiMakerUtils.sizeGuideTmpl(this.imageSize);
    FlexiMakerDomUtils.removeSizeGuide();

    const flexi_maker_board_el = document.querySelector('.flexi-maker-board');
    if (!flexi_maker_board_el) return;

    flexi_maker_board_el.appendChild(size_guide_el);

    const canvas_el = document.querySelector(
      '.canvas_container.selected > .canvas_zoom_wrapper',
    ) as HTMLElement;
    if (canvas_el) {
      const transform = canvas_el.style.transform || window.getComputedStyle(canvas_el).transform;
      const match = transform.match(/scale\(([^)]+)\)/);

      if (match && match[1]) {
        const parent_scale = parseFloat(match[1]) || 1;
        this._setGuideStyle(size_guide_el, parent_scale);
      }
    }
  }

  handleRangeSizeValue(slider_value: number) {
    if (!this.stickerizer) return;

    const { getSizeAtNormalizedScale } = this.stickerizer.getMinMaxScale(this.offsetDistance);
    const size = getSizeAtNormalizedScale(slider_value);
    return size;
  }

  getSizeFormSliderValue(slider_value: number) {
    if (!this.stickerizer) return;

    const { getSizeAtNormalizedScale } = this.stickerizer.getMinMaxScale(this.offsetDistance);
    const size = getSizeAtNormalizedScale(slider_value);
    return size;
  }

  updateCanvasScale(
    canvas_zoom_wrapper: HTMLElement,
    { size, slider_value }: CanvasScaleOptions,
    makeTranslateXY: (canvas_zoom_wrapper: HTMLElement, center: MarkerPositionData) => MarkerPositionData,
  ) {
    if (!this.stickerizer) return;

    FlexiMakerUtils.setCvImageSize(this.data.cv_image, { ...size, slider_value });

    const center = {
      x: canvas_zoom_wrapper.offsetWidth / 2,
      y: canvas_zoom_wrapper.offsetHeight / 2,
    };

    const translate_xy = makeTranslateXY(canvas_zoom_wrapper, center);
    G.mp.maker.zoom_attr.scale = this._is_background_fixed
      ? 1
      : FlexiMakerUtils.calculateScaleFromSlider(slider_value);

    FlexiMakerUtils.adjustScale(
      document.querySelector('.canvas_container.selected .canvas_zoom_wrapper') as HTMLElement,
      G.mp.maker.zoom_attr.scale,
      center,
      translate_xy,
    );

    if (this._use_stroke_dash) {
      const path_el = document.querySelector('.flexi-maker-board > svg > g') as HTMLElement;
      if (path_el) {
        const { thickness, dashA, dashB } = getStrokeStyle(slider_value);
        path_el.style.strokeWidth = `${thickness}px`;
        path_el.style.strokeDasharray = `${dashA}, ${dashB}`;
      }
    }
  }

  async getThumbnailDataURL() {
    if (!this._printable?.thumbnail_canvas) {
      console.warn('썸네일 생성을 위한 캔버스가 없음.');
      return;
    }

    return FlexiMakerUtils.addMarginThumbnailDataUrl(this._printable?.thumbnail_canvas, 1200, 1200);
  }

  async makeMaterials(product: ProductColorFromMaker) {
    try {
      if (!this.stickerizer) {
        await this._getStickerizer();
      }

      if (!this.stickerizer) {
        console.error('Stickerizer 없음.');
        await this._getStickerizer();
        return false;
      }

      const printable = await this.stickerizer.toPrintables({
        materialSource: this?.materialSource,
        ...this.profile.stickerizerOptions?.(),
      });

      if (!printable || !printable.thumbnail_canvas || !printable.design_canvas || !printable.cutline_svg) {
        console.error('Printable 생성 실패.');
        return false;
      }

      this._printable = printable;

      this._svg_product_materials = {
        original_cutting_line_svg_file: await FlexiMakerUtils.createSvgFile(printable.cutline_svg),
        printable_file: await FlexiMakerUtils.canvasToPngFile(printable.design_canvas),
      };

      this._setSvgProductMaterial(product);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  private _getSizeRange(): SizeRange | undefined {
    if (!this.stickerizer) return;

    const { getSizeAtNormalizedScale } = this.stickerizer.getMinMaxScale(this.offsetDistance);

    const { width: min_width, height: min_height } = getSizeAtNormalizedScale(0);
    const { width: max_width, height: max_height } = getSizeAtNormalizedScale(1);

    const result = {
      min_width,
      max_width,
      min_height,
      max_height,
    };

    this._size_range = result;

    return result;
  }

  private _validateStickerizer(stickerizer: Stickerizer): boolean {
    if (!stickerizer.preprocessedInfo) {
      console.warn('Stickerizer가 생성되었지만 preprocessedInfo가 없습니다.');
      return false;
    }
    return true;
  }

  private async _getStickerizer(): Promise<Stickerizer | null> {
    if (!this.stickerizer && this.imageUrl) {
      try {
        this._stickerizer = await Stickerizer.create({
          image_url: `https:${this.imageUrl}`,
          bp_id: this.bpId,
        });

        if (!this._validateStickerizer(this._stickerizer)) {
          this._stickerizer = null;
          return null;
        }
      } catch (error) {
        console.error('Stickerizer 생성 중 오류 발생:', error);
        this._stickerizer = null;
        return null;
      }
    }

    return this.stickerizer;
  }

  private _setGuideStyle(el: HTMLElement, scale: number) {
    const originalHeight = el.offsetHeight;
    el.style.height = `${originalHeight / scale + 8}px`;
    el.style.fontSize = `${1 / scale}rem`;
    el.style.padding = `${0.25 / scale}rem ${1 / scale}rem`;
  }

  private _setSvgProductMaterial(product: ProductColorFromMaker) {
    if (!this.svgProductMaterial) {
      console.warn('material가 없음.');
      return;
    }

    product._.svg_product_materials = [
      {
        ...this.svgProductMaterial,
        process_time: undefined,
        original_svg_file_name: undefined,
      },
    ];
  }
}
